<template>
  <div
    :id="headingId"
    v-editable="blok"
    :class="{
      'heading-icon--sm': !isHeading2,
      'heading-icon--no-icon': !media?.filename,
    }"
    class="heading-icon"
  >
    <MediaImage
      v-if="media?.filename"
      :max-width="iconMaxWidth"
      :media="media"
      :use-aspect="true"
    />
    <component :is="headingType" ref="target">
      {{ blok?.title }}
    </component>
  </div>
</template>

<script setup lang="ts">
import type { InlineHeadingIconStoryblok } from '~/types/storyblok'

const props = defineProps<{
  blok: InlineHeadingIconStoryblok
}>()
const route = useRoute()
const router = useRouter()
const target = ref()
const targetIsVisible = useElementVisibility(target, {
  threshold: 1,
})
watch(targetIsVisible, onElementVisible)

const media = computed(() =>
  props.blok?.icon?.filename ? props.blok.icon : null,
)
const headingId = computed(() => {
  if (!isHeading2.value) return
  return (
    'section-' +
    props.blok.title
      .replace(/[^\w\s]|_/g, '')
      .replace(/\s+/g, '-')
      .toLowerCase()
  )
})
const headingType = computed(() => props.blok.type || 'h2')
const isHeading2 = computed(() => headingType.value === 'h2')
const iconMaxWidth = computed(() => (isHeading2.value ? 80 : 45))

function onElementVisible(isVisible) {
  if (isVisible && isHeading2.value) {
    const hash = '#' + headingId.value
    if (hash !== route?.hash) router.replace({ hash })
  }
}
</script>

<style lang="less">
.heading-icon {
  .flex();
  .align-center();

  margin: 0 0 unit(48/27, em); // 48px;

  @media @desktop {
    margin-left: -@gutter-sm;
  }

  .media {
    .flex-no-shrink();

    flex-basis: @80px;
    margin: 0;
  }

  h2,
  h3 {
    margin: 0 0 0 @gutter-sm;

    @media @desktop {
      margin: 0 0 0 @gutter-lg;
    }
  }

  h3 {
    @media @desktop {
      margin-left: @gutter;
    }
  }

  &.heading-icon--sm {
    margin-left: 0;
    .media {
      flex-basis: @45px;
    }
  }

  &.heading-icon--no-icon {
    h2,
    h3 {
      margin-left: 0;
    }
  }
}
</style>
